import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Grid,
  ButtonBase,
  Typography,
  Tooltip,
  Alert,
  FormHelperText
} from '@mui/material';
import { Person, Edit } from '@mui/icons-material';
import { avatarListSchema } from './schemas';
import type { AvatarPickerProps, AvatarData } from './types';
import { getCollections, capitalize } from './utils';
import { getAvatarUrlWithSas } from '@/app/utils/avatarUtils';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nCommonNs } from '@/i18n/dictionaries/interfaces';

export function AvatarPicker({
  avatars: rawAvatars,
  value: controlledValue,
  onChange,
  variant = 'inline',
  disabled = false,
  error = false,
  helperText,
  label,
  required = false,
  previewSize = 96,
  collections: collectionOverride,
  selectedGender,
  gridColumns = 6,
  className,
  id,
  'aria-label': ariaLabel
}: AvatarPickerProps) {
  const { t: translateCommon } = useTranslation(I18nNamespace.Common);

  // Validate avatars once
  const avatars = useMemo(() => {
    const result = avatarListSchema.safeParse(rawAvatars);
    return result.success ? result.data : [];
  }, [rawAvatars]);

  // Standard controlled/uncontrolled pattern
  const [internalValue, setInternalValue] = useState<string>(avatars[0]?.url || '');
  const value = controlledValue !== undefined ? controlledValue : internalValue;

  const handleChange = useCallback((url: string) => {
    if (controlledValue === undefined) {
      setInternalValue(url);
    }
    onChange?.(url);
  }, [controlledValue, onChange]);

  // Dialog state for dialog variant
  const [dialogOpen, setDialogOpen] = useState(false);
  // Track original value when dialog opens for cancel functionality
  const [originalValue, setOriginalValue] = useState<string>('');

  // Collection management
  const collections = useMemo(() => getCollections(avatars, collectionOverride), [avatars, collectionOverride]);
  
  // Track manual tab selection
  const [manualOverride, setManualOverride] = useState<string | null>(null);

  // Compute active collection based on current avatar or user selection
  const activeCollection = useMemo(() => {
    // Use manual override if user clicked a tab
    if (manualOverride) return manualOverride;
    
    // No collections available
    if (collections.length === 0) return '';
    
    // Find collection of current avatar
    if (value && avatars.length > 0) {
      const currentAvatar = avatars.find(a => a.url === value);
      if (currentAvatar?.collection && collections.includes(currentAvatar.collection)) {
        return currentAvatar.collection;
      }
    }
    
    // Default to "Flat" if available, otherwise first collection
    return collections.includes('Flat') ? 'Flat' : collections[0];
  }, [collections, value, avatars, manualOverride]);

  // Reset manual override when avatar selection changes
  useEffect(() => {
    setManualOverride(null);
  }, [value]);

  // Filter avatars by collection and gender
  const filteredAvatars = useMemo(() => {
    let filtered = avatars;
    
    // Filter by collection
    if (activeCollection && collections.length > 0) {
      filtered = filtered.filter(avatar => avatar.collection === activeCollection);
    }
    
    // Filter by gender if provided
    if (selectedGender) {
      filtered = filtered.filter(avatar => avatar.gender === selectedGender);
    }
    
    return filtered;
  }, [avatars, activeCollection, collections, selectedGender]);

  // Find current avatar
  const currentAvatar = useMemo(() => avatars.find(a => a.url === value), [avatars, value]);

  // Render preview image/placeholder
  const renderPreview = useCallback((size: number) => (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        overflow: 'hidden',
        border: 3,
        borderColor: error ? 'error.main' : value ? 'secondary.main' : 'grey.300',
        borderStyle: value ? 'solid' : 'dashed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: value ? 'transparent' : 'grey.100',
        boxShadow: value ? 2 : 1,
        transition: 'all 0.2s ease-in-out'
      }}
    >
      {value ? (
        <img
          key={value}
          src={getAvatarUrlWithSas(value)}
          alt={currentAvatar?.label || 'Selected avatar'}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      ) : (
        <Person sx={{ fontSize: size * 0.4, color: 'grey.400' }} />
      )}
    </Box>
  ), [value, error, currentAvatar]);

  // Render avatar grid
  const renderAvatarGrid = useCallback(() => (
    <Box>
      {/* Collection tabs */}
      {collections.length > 1 && (
        <Tabs
          value={collections.indexOf(activeCollection)}
          onChange={(_, index) => setManualOverride(collections[index])}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          {collections.map((collection) => (
            <Tab key={collection} label={capitalize(collection)} />
          ))}
        </Tabs>
      )}

      {/* Avatar grid */}
      <Grid container spacing={1}>
        {filteredAvatars.map((avatar) => {
          const isSelected = avatar.url === value;
          return (
            <Grid key={avatar.url} item xs={12 / Math.min(gridColumns, 12)}>
              <ButtonBase
                onClick={() => handleChange(avatar.url)}
                disabled={disabled}
                aria-pressed={isSelected}
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  border: 3,
                  borderColor: isSelected ? 'secondary.main' : 'grey.200',
                  overflow: 'hidden',
                  position: 'relative',
                  aspectRatio: '1 / 1',
                  boxShadow: isSelected ? 4 : 1,
                  transition: 'all 0.2s ease-in-out',
                  bgcolor: isSelected ? 'secondary.50' : 'background.paper',
                  '&:hover': {
                    borderColor: isSelected ? 'secondary.main' : 'secondary.light',
                    transform: 'translateY(-2px)',
                    boxShadow: isSelected ? 6 : 3,
                  },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'secondary.main',
                    outlineOffset: 2,
                  },
                  '&:disabled': {
                    opacity: 0.5,
                  },
                }}
              >
                <img
                  src={getAvatarUrlWithSas(avatar.url)}
                  alt={avatar.label || `${avatar.collection || ''} ${avatar.gender || ''}`.trim() || 'avatar'}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                {isSelected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 6,
                      right: 6,
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      bgcolor: 'secondary.main',
                      border: 2,
                      borderColor: 'background.paper',
                      boxShadow: 2,
                    }}
                  />
                )}
              </ButtonBase>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  ), [collections, activeCollection, filteredAvatars, value, gridColumns, disabled, handleChange]);

  // Dialog variant
  if (variant === 'dialog') {
    return (
      <Box className={className}>
        {label && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {label} {required && '*'}
          </Typography>
        )}
        
        <Tooltip title={disabled ? 'Avatar selection disabled' : 'Click to change avatar'}>
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <ButtonBase
              onClick={() => {
                if (!disabled) {
                  setOriginalValue(value);
                  setDialogOpen(true);
                }
              }}
              disabled={disabled}
              sx={{
                borderRadius: '50%',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: disabled ? 'none' : 'scale(1.05)'
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'secondary.main',
                  outlineOffset: 2,
                }
              }}
            >
              {renderPreview(previewSize)}
            </ButtonBase>
            
            {!disabled && (
              <ButtonBase
                onClick={() => {
                  setOriginalValue(value);
                  setDialogOpen(true);
                }}
                sx={{
                  position: 'absolute',
                  bottom: previewSize * 0.05, // Scale with preview size - moved slightly out
                  right: previewSize * 0.05,  // Scale with preview size - moved slightly out
                  width: Math.max(24, previewSize * 0.2), // Scale button size
                  height: Math.max(24, previewSize * 0.2),
                  borderRadius: '50%',
                  bgcolor: 'secondary.main',
                  color: 'secondary.contrastText',
                  boxShadow: 2,
                  border: 2,
                  borderColor: 'background.paper',
                  '&:hover': {
                    bgcolor: 'secondary.dark',
                    transform: 'scale(1.1)'
                  },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'secondary.main',
                    outlineOffset: 2,
                  }
                }}
              >
                <Edit sx={{ fontSize: Math.max(12, previewSize * 0.15) }} />
              </ButtonBase>
            )}
          </Box>
        </Tooltip>

        {currentAvatar && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
            {currentAvatar.collection && currentAvatar.gender 
              ? `${currentAvatar.collection} • ${currentAvatar.gender}`
              : currentAvatar.collection || currentAvatar.gender || currentAvatar.label || 'Selected avatar'
            }
          </Typography>
        )}

        {helperText && (
          <FormHelperText error={error}>{helperText}</FormHelperText>
        )}

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {label || 'Select Avatar'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ p: 1 }}>
              {renderAvatarGrid()}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              handleChange(originalValue);
              setDialogOpen(false);
            }}>
              {translateCommon(nameof.full<I18nCommonNs>((n) => n.buttonLabels.cancel))}
            </Button>
            <Button 
              onClick={() => setDialogOpen(false)}
              variant="contained"
              disabled={!value}
            >
              {translateCommon(nameof.full<I18nCommonNs>((n) => n.buttonLabels.ok))}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // Inline variant - shows avatar grid directly for selection (like original "Edit Mode")
  return (
    <Box className={className} id={id} aria-label={ariaLabel}>
      {label && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {label} {required && '*'}
        </Typography>
      )}
      
      {/* Show preview at top */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        {renderPreview(previewSize)}
      </Box>

      {/* Avatar selection grid */}
      {renderAvatarGrid()}

      {helperText && (
        <FormHelperText error={error} sx={{ mt: 1 }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
}

export default AvatarPicker;
